import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled, { css, keyframes } from 'styled-components'
import Tooltip from '@mui/material/Tooltip'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import { usePlanningConstraints } from './usePlanningConstraints'
import PlanningConstraintsMap from './PlanningConstraintsMap'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import {
  DATASET_ICONS,
  GROUP_ICONS,
  TONES,
  toneFor,
  tileStatusLabel,
  formatShortDate,
  formatRelative
} from './planningConstraintsMeta'

const STATUS_COPY = {
  pending: 'Queued for screening…',
  syncing: 'Screening against planning.data.gov.uk…',
  rate_limited: 'GOV.UK is rate limiting requests — retrying shortly…',
  synced: 'Screened',
  not_found: 'Location not found',
  failed: 'Screening failed'
}

const PlanningConstraintsPanel = () => {
  const bp = useSelector(getSelectedBp)
  const bpId = bp?.id
  const { dashboard, loading, checking, error, check } = usePlanningConstraints({ bpId })
  const [showAll, setShowAll] = useState(false)
  const [view, setView] = useState('summary')

  const hasUprn = isNotNilOrEmpty(bp?.uprn)
  const status = dashboard?.status || null
  const inProgress = ['pending', 'syncing', 'rate_limited'].includes(status)
  const summary = dashboard?.summary || {}
  const hasCoordinates = typeof dashboard?.location?.latitude === 'number'

  const locationLine = useMemo(() => {
    const location = dashboard?.location || {}
    return [location.localPlanningAuthority, location.ward && `${location.ward} ward`, location.region]
      .filter(Boolean)
      .join(' · ')
  }, [dashboard])

  const flaggedTiles = useMemo(() => {
    if (!dashboard) return []
    return dashboard.groups
      .flatMap(g => g.items.map(i => ({ ...i, groupLabel: g.label })))
      .filter(i => i.status === 'flagged')
      .sort((a, b) => rank(b.severity) - rank(a.severity))
  }, [dashboard])

  const renderBody = () => {
    if (loading) {
      return <Skeleton>Loading planning constraints…</Skeleton>
    }

    if (error) {
      return (
        <EmptyState tone='crit'>
          <EmptyTitle>Could not load planning constraints</EmptyTitle>
          <EmptyCopy>{error}</EmptyCopy>
        </EmptyState>
      )
    }

    if (!hasUprn && !status) {
      return (
        <EmptyState>
          <EmptyIcon><MyLocationOutlinedIcon /></EmptyIcon>
          <EmptyTitle>Add a UPRN to screen this building</EmptyTitle>
          <EmptyCopy>
            Site risks are looked up by the building&apos;s Unique Property Reference Number against
            the GOV.UK planning data platform. Set the address and UPRN in the building details first.
          </EmptyCopy>
        </EmptyState>
      )
    }

    if (!status) {
      return (
        <EmptyState>
          <EmptyIcon><TravelExploreOutlinedIcon /></EmptyIcon>
          <EmptyTitle>This building has not been screened yet</EmptyTitle>
          <EmptyCopy>
            Check UPRN {bp.uprn} against flood zones, heritage designations, planning controls and
            environmental constraints published on planning.data.gov.uk.
          </EmptyCopy>
          <PrimaryButton type='button' onClick={check} disabled={checking}>
            <RefreshIcon /> Screen now
          </PrimaryButton>
        </EmptyState>
      )
    }

    if (status === 'not_found' && !summary.flagged) {
      return (
        <EmptyState tone='warn'>
          <EmptyIcon><MyLocationOutlinedIcon /></EmptyIcon>
          <EmptyTitle>planning.data.gov.uk could not place this building</EmptyTitle>
          <EmptyCopy>
            The platform only covers England, and the UPRN must be present in its address index.
            Check the UPRN is correct or that the address has coordinates, then try again.
          </EmptyCopy>
          <PrimaryButton type='button' onClick={check} disabled={checking}>
            <RefreshIcon /> Try again
          </PrimaryButton>
        </EmptyState>
      )
    }

    if (status === 'failed' && !dashboard?.lastCheckedAt) {
      return (
        <EmptyState tone='crit'>
          <EmptyTitle>The screening failed</EmptyTitle>
          <EmptyCopy>GOV.UK did not respond in time. This is usually temporary.</EmptyCopy>
          <PrimaryButton type='button' onClick={check} disabled={checking}>
            <RefreshIcon /> Try again
          </PrimaryButton>
        </EmptyState>
      )
    }

    if (inProgress && !dashboard?.lastCheckedAt) {
      return (
        <EmptyState>
          <Spinner />
          <EmptyTitle>{STATUS_COPY[status]}</EmptyTitle>
          <EmptyCopy>This usually takes a few seconds. The page updates automatically.</EmptyCopy>
        </EmptyState>
      )
    }

    return (
      <>
        <RiskStrip summary={summary} />

        {flaggedTiles.length > 0 && (
          <Section>
            <SectionHeader>
              <SectionTitle>Flagged for this building</SectionTitle>
              <SectionMeta>{flaggedTiles.length} of {summary.checked} checks</SectionMeta>
            </SectionHeader>
            <TileGrid>
              {flaggedTiles.map(item => (
                <Tile key={item.dataset} item={item} defaultOpen={item.severity === 'critical'} />
              ))}
            </TileGrid>
          </Section>
        )}

        {flaggedTiles.length === 0 && (
          <EmptyState tone='ok' $compact>
            <EmptyTitle>No active planning constraints found</EmptyTitle>
            <EmptyCopy>
              None of the {summary.checked} national and local designations we screen against apply to this
              location. Locally supplied datasets may be incomplete — see the full breakdown below.
            </EmptyCopy>
          </EmptyState>
        )}

        <ToggleAll type='button' onClick={() => setShowAll(v => !v)} aria-expanded={showAll}>
          <KeyboardArrowDownIcon style={{ transform: showAll ? 'rotate(180deg)' : 'none' }} />
          {showAll ? 'Hide full breakdown' : `Show all ${summary.checked} checks by category`}
        </ToggleAll>

        {showAll && dashboard.groups.map(group => {
          const GroupIcon = GROUP_ICONS[group.key] || InfoOutlinedIcon
          return (
            <Section key={group.key}>
              <SectionHeader>
                <SectionTitle>
                  <GroupIcon fontSize='small' />
                  {group.label}
                </SectionTitle>
                <SectionMeta>
                  {group.flagged > 0
                    ? `${group.flagged} flagged`
                    : 'Nothing flagged'}
                </SectionMeta>
              </SectionHeader>
              <GroupDescription>{group.description}</GroupDescription>
              <TileGrid>
                {group.items.map(item => <Tile key={item.dataset} item={item} />)}
              </TileGrid>
            </Section>
          )
        })}

        <Footnote>
          <InfoOutlinedIcon fontSize='inherit' />
          <span>
            Source: <a href='https://www.planning.data.gov.uk' target='_blank' rel='noreferrer'>planning.data.gov.uk</a>
            {' '}(MHCLG). National datasets (Environment Agency, Historic England, Natural England, DEFRA) are complete;
            datasets marked &ldquo;None recorded&rdquo; are supplied by individual councils and may not be published yet.
            This is a screening aid, not a substitute for a formal planning or legal search.
          </span>
        </Footnote>
      </>
    )
  }

  return (
    <Page data-tour-id='folders.site-risks'>
      <Header>
        <HeaderLeft>
          <Title>Site risks &amp; planning constraints</Title>
          <Subtitle>
            {hasUprn ? `UPRN ${bp.uprn}` : 'No UPRN'}
            {locationLine && ` · ${locationLine}`}
          </Subtitle>
          <Meta>
            {status && (
              <Chip tone={chipTone(status)}>
                {inProgress && <Spinner $small />}
                {STATUS_COPY[status]}
                {status === 'synced' && dashboard?.lastCheckedAt && ` · ${formatRelative(dashboard.lastCheckedAt)}`}
              </Chip>
            )}
            {dashboard?.lookupMethod === 'point' && (
              <Tooltip title='The UPRN was not in the GOV.UK index, so the building coordinates were used instead.'>
                <Chip tone='neutral'>Matched by coordinates</Chip>
              </Tooltip>
            )}
            {dashboard?.location?.source === 'postcode' && (
              <Tooltip title='This building has no exact coordinates on file, so the postcode centroid is used. That can be tens of metres out — small designations (listed buildings, TPOs) may be missed. Re-save the address from the UPRN lookup to fix.'>
                <Chip tone='warn'>Location approximated from postcode</Chip>
              </Tooltip>
            )}
          </Meta>
        </HeaderLeft>
        <HeaderActions>
          {hasCoordinates && !loading && (
            <ViewSwitch role='tablist' aria-label='View'>
              <ViewButton type='button' role='tab' aria-selected={view === 'summary'} $active={view === 'summary'} onClick={() => setView('summary')}>
                <DashboardOutlinedIcon /> Summary
              </ViewButton>
              <ViewButton data-tour-id='planning.map-toggle' type='button' role='tab' aria-selected={view === 'map'} $active={view === 'map'} onClick={() => setView('map')}>
                <MapOutlinedIcon /> Map
              </ViewButton>
            </ViewSwitch>
          )}
          {dashboard?.mapUrl && (
            <SecondaryButton as='a' href={dashboard.mapUrl} target='_blank' rel='noreferrer'>
              <OpenInNewIcon /> View on GOV.UK map
            </SecondaryButton>
          )}
          {(hasUprn || status) && (
            <SecondaryButton type='button' onClick={check} disabled={checking || inProgress}>
              <RefreshIcon className={checking || inProgress ? 'spin' : ''} />
              {checking || inProgress ? 'Checking…' : 'Re-check'}
            </SecondaryButton>
          )}
        </HeaderActions>
      </Header>

      {view === 'map' && hasCoordinates ? (
        <>
          <PlanningConstraintsMap dashboard={dashboard} />
          <Footnote style={{ marginTop: 12 }}>
            <InfoOutlinedIcon fontSize='inherit' />
            <span>
              Layers are streamed live from planning.data.gov.uk, so the map can show designations near the
              building that do not apply to it. Datasets flagged for this building are switched on by default;
              click any shape for its record. Faded shapes are designations that have ended.
            </span>
          </Footnote>
        </>
      ) : renderBody()}
    </Page>
  )
}

export default PlanningConstraintsPanel

// ---------------------------------------------------------------------------

const rank = severity => ({ critical: 3, warning: 2, info: 1 }[severity] || 0)

// The "official record" links are published by the data owners and are outside
// our control: Historic England geo-blocks non-UK visitors, and council pages
// get moved. The planning.data.gov.uk entity page is the reliable link.
const externalLinkHint = (url, source) => {
  if (/historicengland\.org\.uk/i.test(url)) {
    return 'Historic England\u2019s website only accepts visitors from UK IP addresses.'
  }
  return `External page published by ${source}. Council pages are occasionally moved or removed.`
}

const chipTone = status => {
  if (status === 'synced') return 'ok'
  if (status === 'failed') return 'crit'
  if (status === 'not_found') return 'warn'
  return 'neutral'
}

const RiskStrip = ({ summary }) => {
  const total = summary.checked || 0
  const segments = [
    ['crit', summary.critical || 0],
    ['warn', summary.warning || 0],
    ['info', summary.info || 0],
    ['muted', summary.historic || 0],
    ['ok', summary.clear || 0],
    ['neutral', summary.noneRecorded || 0]
  ]

  return (
    <StatsCard data-tour-id='planning.summary'>
      <StatsRow>
        <Stat tone={summary.flagged > 0 ? (summary.critical > 0 ? 'crit' : 'warn') : 'ok'}>
          <StatValue>{summary.flagged || 0}</StatValue>
          <StatLabel>Flagged</StatLabel>
        </Stat>
        <Stat tone={summary.critical > 0 ? 'crit' : undefined}>
          <StatValue>{summary.critical || 0}</StatValue>
          <StatLabel>Critical</StatLabel>
        </Stat>
        <Stat tone={summary.warning > 0 ? 'warn' : undefined}>
          <StatValue>{summary.warning || 0}</StatValue>
          <StatLabel>Warning</StatLabel>
        </Stat>
        <Stat tone='ok'>
          <StatValue>{summary.clear || 0}</StatValue>
          <StatLabel>Clear</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{summary.noneRecorded || 0}</StatValue>
          <StatLabel>None recorded</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{total}</StatValue>
          <StatLabel>Checks</StatLabel>
        </Stat>
      </StatsRow>
      <Meter aria-label='Breakdown of checks by outcome'>
        {segments.map(([tone, n]) => n > 0 && (
          <Tooltip key={tone} title={`${TONES[tone].label}: ${n}`}>
            <MeterSegment tone={tone} style={{ flex: n }} />
          </Tooltip>
        ))}
      </Meter>
    </StatsCard>
  )
}

const Tile = ({ item, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen)
  const tone = toneFor(item.status, item.severity)
  const Icon = DATASET_ICONS[item.dataset] || InfoOutlinedIcon
  const hasDetails = item.entities?.length > 0
  const statusLabel = tileStatusLabel(item)

  return (
    <TileCard tone={tone}>
      <TileHeader
        as={hasDetails ? 'button' : 'div'}
        type={hasDetails ? 'button' : undefined}
        onClick={hasDetails ? () => setOpen(v => !v) : undefined}
        aria-expanded={hasDetails ? open : undefined}
      >
        <TileIcon tone={tone}><Icon /></TileIcon>
        <TileText>
          <TileLabel>{item.label}</TileLabel>
          <TileStatus tone={tone}>{statusLabel}</TileStatus>
        </TileText>
        {item.status === 'none_recorded' && (
          <Tooltip title={`Supplied by ${item.source}. Many councils have not published this dataset yet, so "none recorded" does not guarantee the constraint is absent.`}>
            <Hint><InfoOutlinedIcon fontSize='inherit' /></Hint>
          </Tooltip>
        )}
        {hasDetails && (
          <Chevron $open={open}><KeyboardArrowDownIcon fontSize='small' /></Chevron>
        )}
      </TileHeader>
      <TileBar tone={tone} />

      {open && hasDetails && (
        <TileBody>
          <TileDescription>{item.description}</TileDescription>
          {item.entities.map(entity => (
            <Entity key={entity.id} $historic={entity.isHistoric}>
              <EntityMain>
                <EntityName>
                  {entity.name || item.label}
                  {entity.isHistoric && <HistoricTag>no longer applies</HistoricTag>}
                </EntityName>
                <EntityMeta>
                  {entity.reference && <span>Ref {entity.reference}</span>}
                  {entity.startDate && <span>since {formatShortDate(entity.startDate)}</span>}
                  {entity.endDate && <span>until {formatShortDate(entity.endDate)}</span>}
                  <span>{item.source}</span>
                </EntityMeta>
                {(entity.properties || []).map(({ key, label, value }) => (
                  <Property key={key}>
                    <PropertyKey>{label}</PropertyKey>
                    <PropertyValue>{value}</PropertyValue>
                  </Property>
                ))}
              </EntityMain>
              <EntityLinks>
                <a href={entity.entityUrl} target='_blank' rel='noreferrer'>
                  View on planning.data.gov.uk <OpenInNewIcon fontSize='inherit' />
                </a>
                {entity.documentationUrl && (
                  <Tooltip title={externalLinkHint(entity.documentationUrl, item.source)}>
                    <a href={entity.documentationUrl} target='_blank' rel='noreferrer'>
                      {item.source} record <OpenInNewIcon fontSize='inherit' />
                    </a>
                  </Tooltip>
                )}
              </EntityLinks>
            </Entity>
          ))}
        </TileBody>
      )}
    </TileCard>
  )
}

// ---------------------------------------------------------------------------

const spin = keyframes`to { transform: rotate(360deg); }`

const card = css`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
`

const Page = styled.div`
  background: rgb(247, 248, 253);
  padding: 24px;
  border-radius: 8px;
  color: #333;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const Header = styled.div`
  ${card};
  padding: 24px 30px;
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 18px 20px;
  }
`

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 240px;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #0c1229;
`

const Subtitle = styled.div`
  font-size: 13px;
  color: #777;
  margin-bottom: 12px;
`

const Meta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ tone }) => TONES[tone || 'neutral'].bg};
  color: ${({ tone }) => TONES[tone || 'neutral'].fg};
  border: 1px solid ${({ tone }) => TONES[tone || 'neutral'].border};
`

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  line-height: 1;
  white-space: nowrap;

  svg { font-size: 16px; }
  svg.spin { animation: ${spin} 1s linear infinite; }

  &:disabled { opacity: 0.6; cursor: default; }
`

const PrimaryButton = styled.button`
  ${buttonBase};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  margin-top: 16px;
`

const SecondaryButton = styled.button`
  ${buttonBase};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: #fff;
  color: #0c1229;

  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.colors.secondary}; }
`

const ViewSwitch = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`

const ViewButton = styled.button`
  ${buttonBase};
  border: 0;
  border-radius: 0;
  padding: 9px 12px;
  background: ${({ $active, theme }) => ($active ? theme.colors.secondary : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#0c1229')};

  & + & { border-left: 1px solid ${({ theme }) => theme.colors.lightGrey}; }
`

const StatsCard = styled.section`
  ${card};
  padding: 18px 22px;
  margin-bottom: 16px;
`

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
`

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 6px;
  background: ${({ tone }) => (tone ? TONES[tone].bg : '#fcfdfe')};
  border: 1px solid ${({ tone }) => (tone ? TONES[tone].border : '#f2f2f2')};
  color: ${({ tone }) => (tone ? TONES[tone].fg : '#0c1229')};
`

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
`

const StatLabel = styled.div`
  font-size: 11px;
  color: #666;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  font-weight: 500;
`

const Meter = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: #f2f2f2;
  gap: 2px;
`

const MeterSegment = styled.div`
  background: ${({ tone }) => TONES[tone].bar};
  min-width: 4px;
`

const Section = styled.section`
  margin-bottom: 20px;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 4px;
`

const SectionTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #0c1229;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;

  svg { color: #999; }
`

const SectionMeta = styled.div`
  font-size: 12px;
  color: #999;
`

const GroupDescription = styled.p`
  font-size: 12px;
  color: #888;
  margin: 0 0 10px 0;
`

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 12px;
  align-items: start;
`

const TileCard = styled.div`
  ${card};
  border: 1px solid ${({ tone }) => TONES[tone].border};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const TileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 12px 14px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  font: inherit;
  color: inherit;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
`

const TileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ tone }) => TONES[tone].bg};
  color: ${({ tone }) => TONES[tone].fg};

  svg { font-size: 22px; }
`

const TileText = styled.div`
  flex: 1;
  min-width: 0;
`

const TileLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #0c1229;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TileStatus = styled.div`
  font-size: 12px;
  margin-top: 2px;
  color: ${({ tone }) => TONES[tone].fg};
  font-weight: ${({ tone }) => (tone === 'crit' || tone === 'warn' ? 600 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TileBar = styled.div`
  height: 4px;
  background: ${({ tone }) => TONES[tone].bar};
  opacity: ${({ tone }) => (tone === 'neutral' ? 0.5 : 1)};
`

const Hint = styled.span`
  color: #b3b3b3;
  font-size: 16px;
  display: inline-flex;
`

const Chevron = styled.span`
  color: #999;
  display: inline-flex;
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
`

const TileBody = styled.div`
  padding: 12px 14px 14px 14px;
  border-top: 1px solid #f2f2f2;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TileDescription = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #555;
`

const Entity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fafbfd;
  border: 1px solid #f2f2f2;
  opacity: ${({ $historic }) => ($historic ? 0.7 : 1)};
`

const EntityMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const EntityName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #0c1229;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`

const HistoricTag = styled.span`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 2px 6px;
  border-radius: 8px;
  background: ${TONES.muted.bg};
  color: ${TONES.muted.fg};
  border: 1px solid ${TONES.muted.border};
`

const EntityMeta = styled.div`
  font-size: 11px;
  color: #888;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const Property = styled.div`
  display: flex;
  gap: 8px;
  font-size: 11px;
  line-height: 1.4;
`

const PropertyKey = styled.span`
  color: #999;
  min-width: 90px;
  flex-shrink: 0;
`

const PropertyValue = styled.span`
  color: #333;
  word-break: break-word;
`

const EntityLinks = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 11px;

  a {
    color: ${({ theme }) => theme.colors.secondary};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
  a:hover { text-decoration: underline; }
`

const ToggleAll = styled.button`
  ${buttonBase};
  border: 1px dashed ${({ theme }) => theme.colors.grey[600]};
  background: transparent;
  color: #555;
  width: 100%;
  justify-content: center;
  margin: 4px 0 20px 0;
  padding: 11px;

  svg { transition: transform 0.15s ease; }
`

const EmptyState = styled.div`
  ${card};
  border: 1px solid ${({ tone }) => (tone ? TONES[tone].border : '#f2f2f2')};
  background: ${({ tone }) => (tone ? TONES[tone].bg : '#fff')};
  padding: ${({ $compact }) => ($compact ? '20px 24px' : '40px 24px')};
  text-align: center;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const EmptyIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.linen};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;

  svg { font-size: 28px; }
`

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0c1229;
  margin-bottom: 6px;
`

const EmptyCopy = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #666;
  max-width: 520px;
`

const Skeleton = styled.div`
  ${card};
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 13px;
`

const Spinner = styled.div`
  width: ${({ $small }) => ($small ? '12px' : '28px')};
  height: ${({ $small }) => ($small ? '12px' : '28px')};
  border: ${({ $small }) => ($small ? '2px' : '3px')} solid #e6e6e6;
  border-top-color: ${({ theme }) => theme.colors.secondary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: ${({ $small }) => ($small ? 0 : '14px')};
  flex-shrink: 0;
`

const Footnote = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 11px;
  line-height: 1.5;
  color: #888;
  padding: 0 4px;

  svg { margin-top: 2px; flex-shrink: 0; }
  a { color: inherit; }
`
